import { useDispatch, useSelector } from "react-redux";
import { useMemo } from "react";
import type { RootState } from "@/store/store";
import { actions } from "@/store/slices/apartmentFiltersSlice";
import type { StatFilter } from "@/hooks/useAggregatedStats";
import type { Apartment } from "@/models/responses";

/**
 * Traceability: FR/AS IDs reference
 * .verified/features/apartment-filters-everywhere/spec.md (this hook is shared
 * filter infra). The "active as of today" selector below belongs to
 * apartment-decommissioning (FR-008, AS-6/AS-7).
 */

/**
 * "Active as of today" selector: active iff `dateRemoved == null || dateRemoved
 * > today`. Half-open — a removal dated exactly today makes it inactive; a
 * future-dated removal stays active until that date, then flips with no user
 * action. Exported so apartment pickers reuse this exact rule rather than the
 * flat `dateRemoved == null` test. `today` is injected because the app has no
 * central clock abstraction. (apartment-decommissioning FR-008)
 */
export function isActiveNow(apartment: Pick<Apartment, "dateRemoved">, today: Date): boolean {
    return apartment.dateRemoved == null || apartment.dateRemoved > today;
}

export interface UseApartmentFilters {
    filters: {
        bedrooms: number;
        municipalitySectionId: number;
        ownerGroupId: number;
        groupId: number;
        tagIds: number[];
        apartmentIds: number[];
        showDecommissioned: boolean;
    };
    setBedrooms: (value: number) => void;
    setMunicipalitySection: (value: number) => void;
    setOwnerGroup: (value: number) => void;
    setGroup: (value: number) => void;
    setTagIds: (ids: number[]) => void;
    setApartmentIds: (ids: number[]) => void;
    setShowDecommissioned: (value: boolean) => void;
    clearAll: () => void;
    /**
     * Predicates ready for `useAggregatedStats(..., filters)` or
     * `Array.prototype.filter`. Memoised on the filter-state tuple for a stable
     * per-render reference (avoids the EC-004 load-race where a transient
     * predicate array could yield stale data).
     */
    predicates: StatFilter[];
}

/**
 * Shared apartment-filter hook used by every Tier 1 dashboard page.
 *
 * Reads from and writes to the `apartmentFilters` Redux slice; predicates are
 * derived from the current filter state using the same switch-based logic that
 * lived inline in `pages/overview.tsx` prior to this extraction.
 */
export function useApartmentFilters(): UseApartmentFilters {
    const filters = useSelector((state: RootState) => state.apartmentFilters);
    const dispatch = useDispatch();

    const predicates = useMemo<StatFilter[]>(() => {
        const preds: StatFilter[] = [];

        switch (filters.bedrooms) {
            case 0:
                break;
            case 1:
                preds.push((a) => a.numberOfBeds === 1);
                break;
            case 2:
                preds.push((a) => a.numberOfBeds !== undefined && a.numberOfBeds >= 2);
                break;
        }

        switch (filters.municipalitySectionId) {
            case 0:
                break;
            default:
                preds.push((a) => a.municipalitySectionId === filters.municipalitySectionId);
                break;
        }

        switch (filters.ownerGroupId) {
            case 0:
                break;
            default:
                preds.push((a) => a.ownerGroupId === filters.ownerGroupId);
                break;
        }

        // Apartment group (a portfolio grouping) — distinct from Owner Group.
        switch (filters.groupId) {
            case 0:
                break;
            default:
                preds.push((a) => a.groupId === filters.groupId);
                break;
        }

        if (filters.tagIds.length > 0) {
            const selected = filters.tagIds;
            preds.push((a) => (a.tagIds === undefined ? false : a.tagIds.some((tagId) => selected.includes(tagId))));
        }

        // Explicit apartment picks: an empty selection means "no filter" (the
        // whole portfolio), a non-empty one narrows to exactly those apartments.
        if (filters.apartmentIds.length > 0) {
            const selected = filters.apartmentIds;
            preds.push((a) => selected.includes(a.id));
        }

        // Toggle OFF → keep only apartments active as of today (past/today
        // removal drops off, future-dated stays). ON → no predicate, so
        // decommissioned apartments are revealed. (apartment-decommissioning
        // AS-6/AS-7)
        if (!filters.showDecommissioned) {
            const today = new Date();
            preds.push((a) => isActiveNow(a, today));
        }

        return preds;
    }, [
        filters.bedrooms,
        filters.municipalitySectionId,
        filters.ownerGroupId,
        filters.groupId,
        filters.tagIds,
        filters.apartmentIds,
        filters.showDecommissioned,
    ]);

    return {
        filters,
        setBedrooms: (value: number) => dispatch(actions.setBedrooms(value)),
        setMunicipalitySection: (value: number) => dispatch(actions.setMunicipalitySection(value)),
        setOwnerGroup: (value: number) => dispatch(actions.setOwnerGroup(value)),
        setGroup: (value: number) => dispatch(actions.setGroup(value)),
        setTagIds: (ids: number[]) => dispatch(actions.setTagIds(ids)),
        setApartmentIds: (ids: number[]) => dispatch(actions.setApartmentIds(ids)),
        setShowDecommissioned: (value: boolean) => dispatch(actions.setShowDecommissioned(value)),
        clearAll: () => dispatch(actions.clearAll()),
        predicates,
    };
}

export default useApartmentFilters;
